import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Filter } from '../components/Modal/TaskFiltersModal';
import { getDefaultFilters, updateWithSpaceDefaultFilters } from '../components/TaskManagement/helpers/filterHelpers';
import {
  saveFiltersToLocalStorage,
  loadFiltersFromLocalStorage,
  clearFiltersFromLocalStorage,
} from '../common/localStorage';
import { WorkspaceView, WorkspaceViewEntityType, WorkspaceViewGroup } from '../types/WorkspaceView';
import { TaskStatus } from '../types/TaskStatus';
import { User } from '../types/User';
import { Epic } from '../types/Epic';
import { SpaceLabel } from '../types/TaskLabel';
import { WorkspaceEstimationType, WorkspaceFramework } from '../types/Workspace';

interface FilterState {
  workspaceId: number;
  viewId: number;
  filters: Filter[];
}

interface FilterStore {
  // State
  filterStates: Record<string, FilterState>;

  // Actions
  initializeFilters: (
    workspaceId: number,
    viewId: number,
    epics: Epic[],
    users: User[],
    statuses: TaskStatus[],
    labels: SpaceLabel[],
    estimationType?: WorkspaceEstimationType,
    framework?: WorkspaceFramework,
    group?: WorkspaceViewGroup,
    entityType?: WorkspaceViewEntityType,
    viewFilters?: Filter[]
  ) => void;

  setFilters: (workspaceId: number, viewId: number, filters: Filter[]) => void;

  resetFilters: (
    workspaceId: number,
    viewId: number,
    epics: Epic[],
    users: User[],
    statuses: TaskStatus[],
    labels: SpaceLabel[],
    estimationType?: WorkspaceEstimationType,
    framework?: WorkspaceFramework,
    group?: WorkspaceViewGroup,
    entityType?: WorkspaceViewEntityType,
    viewFilters?: Filter[]
  ) => void;

  getFilters: (workspaceId: number, viewId: number) => Filter[];
  getSelectedFilters: (workspaceId: number, viewId: number) => Filter[];

  // Per-view search text, kept here (not in component state) so it survives
  // navigating into a task and back to the same view (#2276). In-memory only —
  // cleared on full reload, like the rest of the store.
  searchStates: Record<string, string>;
  getSearch: (workspaceId: number, viewId: number) => string;
  setSearch: (workspaceId: number, viewId: number, value: string) => void;
}

const getStateKey = (workspaceId: number, viewId: number): string => `${workspaceId}-${viewId}`;

export const useFilterStore = create<FilterStore>()(
  devtools(
    (set, get) => ({
      filterStates: {},

      initializeFilters: (
        workspaceId,
        viewId,
        epics,
        users,
        statuses,
        labels,
        estimationType,
        framework = WorkspaceFramework.none,
        group = WorkspaceViewGroup.backlog,
        entityType = WorkspaceViewEntityType.task,
        viewFilters
      ) => {
        const key = getStateKey(workspaceId, viewId);
        const existingState = get().filterStates[key];

        // If the store already has filters for this view (e.g. switching back
        // from another space), keep the current state. This prevents stale
        // localStorage data from overwriting the correct in-memory state.
        // Option updates (new users/epics) are handled by updateCurrentFilters.
        if (existingState && existingState.filters.length > 0) {
          return;
        }

        // Generate default filters
        let defaultFilters = getDefaultFilters(
          epics,
          users,
          statuses,
          labels,
          estimationType,
          framework,
          group,
          entityType
        );

        // Try to load from localStorage first (preserves unsaved changes
        // across page refreshes), then fallback to view filters from server
        const savedFilters = loadFiltersFromLocalStorage(workspaceId, viewId);
        if (savedFilters) {
          defaultFilters = updateWithSpaceDefaultFilters(defaultFilters, savedFilters);
        } else if (viewFilters) {
          defaultFilters = updateWithSpaceDefaultFilters(defaultFilters, viewFilters);
        }

        set((state) => ({
          filterStates: {
            ...state.filterStates,
            [key]: {
              workspaceId,
              viewId,
              filters: defaultFilters,
            },
          },
        }));
      },

      setFilters: (workspaceId, viewId, filters) => {
        const key = getStateKey(workspaceId, viewId);

        // Save to localStorage
        saveFiltersToLocalStorage(workspaceId, viewId, filters);

        set((state) => ({
          filterStates: {
            ...state.filterStates,
            [key]: {
              ...state.filterStates[key],
              workspaceId,
              viewId,
              filters,
            },
          },
        }));
      },

      resetFilters: (
        workspaceId,
        viewId,
        epics,
        users,
        statuses,
        labels,
        estimationType,
        framework = WorkspaceFramework.none,
        group = WorkspaceViewGroup.backlog,
        entityType = WorkspaceViewEntityType.task,
        viewFilters
      ) => {
        // Clear localStorage
        clearFiltersFromLocalStorage(workspaceId, viewId);

        // Generate fresh default filters
        let defaultFilters = getDefaultFilters(
          epics,
          users,
          statuses,
          labels,
          estimationType,
          framework,
          group,
          entityType
        );

        // Apply view filters if they exist
        if (viewFilters) {
          defaultFilters = updateWithSpaceDefaultFilters(defaultFilters, viewFilters);
        }

        const key = getStateKey(workspaceId, viewId);
        set((state) => ({
          filterStates: {
            ...state.filterStates,
            [key]: {
              ...state.filterStates[key],
              workspaceId,
              viewId,
              filters: defaultFilters,
            },
          },
        }));
      },

      getFilters: (workspaceId, viewId) => {
        const key = getStateKey(workspaceId, viewId);
        return get().filterStates[key]?.filters || [];
      },

      getSelectedFilters: (workspaceId, viewId) => {
        const filters = get().getFilters(workspaceId, viewId);
        return filters.filter((filter) => {
          const selectedOptions = filter.options?.filter((option) => option.selected) ?? [];
          return selectedOptions.length > 0;
        });
      },

      searchStates: {},

      getSearch: (workspaceId, viewId) => get().searchStates[getStateKey(workspaceId, viewId)] ?? '',

      setSearch: (workspaceId, viewId, value) =>
        set((state) => ({
          searchStates: {
            ...state.searchStates,
            [getStateKey(workspaceId, viewId)]: value,
          },
        })),
    }),
    { name: 'filter-store' }
  )
);
